/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, createContext, forwardRef, isValidElement } from 'react';
import { type ContextValue, DEFAULT_SLOT, Provider, ToggleButton as ToggleButtonCore, type ToggleButtonProps as ToggleButtonCoreProps, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { IconContext } from '../icon/icon.component';
import { Text, TextContext, type TextProps } from '../text/text.component';

interface ToggleButtonProps extends Omit<ToggleButtonCoreProps, 'style' | 'className'> {
  /** The variant of the toggle button. (Default: filled) */
  variant?: keyof typeof variantVariants;
  /** The size variant of the toggle button. (Default: 28) */
  size?: keyof typeof sizeVariants;
  /** Whether the toggle button is rounded. (Default: false) */
  isRounded?: boolean;
  style?: stylex.StyleXStyles;
}

const ToggleButtonContext = createContext<ContextValue<ToggleButtonProps, HTMLButtonElement>>({});

const ToggleButton = forwardRef((props: ToggleButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
  [props, ref] = useContextProps(props, ref, ToggleButtonContext);

  const { style, size = 28, isRounded = false, variant = 'filled', ...coreProps } = props;

  return (
    <ToggleButtonCore
      ref={ref}
      {...stylex.props(styles.base, sizeVariants[size], isRounded && styles.isRounded, variantVariants[variant], style)}
      {...coreProps}>
      {(renderProps) => {
        const resolvedChildren = typeof coreProps.children === 'function' ? coreProps.children(renderProps) : coreProps.children;

        const isTextOnly = Children.toArray(resolvedChildren).every(c => !isValidElement(c));

        const contentColor = (
          renderProps.isDisabled && 'disabled' ||
          (
            variant === 'outlined' &&
            (
              renderProps.isSelected && 'interactive' ||
              renderProps.isHovered && 'tertiary' ||
              'quaternary'
            )
          ) ||
          'interactive'
        );

        return (
          <Provider
            values={[
              [TextContext, {
                size: TEXT_SIZES[size],
                color: contentColor,
              }],
              [IconContext, {
                slots: {
                  [DEFAULT_SLOT]: {
                    style: iconStyles.base,
                    color: contentColor,
                  },
                  [TOGGLE_BUTTON_SLOTS.LEFT_ICON]: {
                    size: LEFT_ICON_SIZE[size],
                    color: contentColor,
                    style: iconStyles.base,
                  },
                  [TOGGLE_BUTTON_SLOTS.RIGHT_ICON]: {
                    size: RIGHT_ICON_SIZE[size],
                    color: contentColor,
                    style: iconStyles.base,
                  }
                }
              }],
            ]}>
            {typeof resolvedChildren === 'string' || isTextOnly ? <Text>{resolvedChildren}</Text> : resolvedChildren}
          </Provider>
        );
      }}
    </ToggleButtonCore>
  );
});

const TEXT_SIZES: Record<keyof typeof sizeVariants, TextProps['size']> = {
  36: 14,
  32: 14,
  28: 12,
  24: 12,
  20: 11,
};


const LEFT_ICON_SIZE: Record<keyof typeof sizeVariants, number> = {
  20: 12,
  24: 12,
  28: 12,
  32: 16,
  36: 16,

};

const RIGHT_ICON_SIZE: Record<keyof typeof sizeVariants, number> = {
  20: 10,
  24: 12,
  28: 12,
  32: 12,
  36: 12,
};

const styles = stylex.create({
  base: {
    borderStyle: 'solid',
    borderWidth: 1,
    gap: 5,
    textDecoration: 'none',
    alignItems: 'center',
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
      ':is([data-pending])': 'auto',
    },
    display: 'inline-flex',
    isolation: 'isolate',
    position: 'relative',
    transitionDuration: '0.2s',
    transitionProperty: 'border-color, background-color',
    transitionTimingFunction: 'ease-in-out',
    userSelect: 'none',
  },
  isRounded: {
    borderRadius: 999,
  }
});

const variantVariants = stylex.create({
  filled: {
    borderColor: {
      default: 'transparent',
      ':is([data-disabled])': 'transparent',
      ':is([data-selected])': $.borderInteractive,
    },
    backgroundColor: {
      default: $.buttonSurfaceTertiary,
      ':is([data-disabled])': $.buttonSurfaceDisabled,
      ':is([data-hovered])': $.buttonSurfaceTertiaryHover,
      ':is([data-selected])': $.buttonSurfaceTertiary,
    },
  },
  outlined: {
    borderColor: {
      default: $.border10,
      ':is([data-disabled])': `${$.border08} !important`,
      ':is([data-hovered])': $.border24,
      ':is([data-selected])': `${$.borderInteractive} !important`,
    },
    backgroundColor: 'transparent',
  }
});

const sizeVariants = stylex.create({
  36: {
    borderRadius: 8,
    paddingBlock: 9,
    paddingInline: 12,
    height: 36,
  },
  32: {
    borderRadius: 8,
    paddingBlock: 7,
    paddingInline: 12,
    height: 32,
  },
  28: {
    borderRadius: 6,
    paddingBlock: 6,
    paddingInline: 10,
    height: 28,
  },
  24: {
    borderRadius: 6,
    paddingBlock: 4,
    paddingInline: 8,
    height: 24,
  },
  20: {
    borderRadius: 4,
    paddingBlock: 0,
    paddingInline: 8,
    height: 20,
  },
});

const iconStyles = stylex.create({
  base: {
    transitionDuration: '0.2s',
    transitionProperty: 'stroke, fill',
    transitionTimingFunction: 'easy-in-out',
  },
});

const TOGGLE_BUTTON_SLOTS = {
  LEFT_ICON: 'leftIcon',
  RIGHT_ICON: 'rightIcon',
};

export { TOGGLE_BUTTON_SLOTS, ToggleButton, ToggleButtonContext, type ToggleButtonProps };
